import { useState, useEffect, FC } from "react";
import { connectRange } from "react-instantsearch-dom";
import { useTheme, Theme } from "@emotion/react";
import { Range } from "rc-slider";
import { Text } from "@otrium/atoms";
import { Box, Flex } from "@otrium/core";
import { Price } from "src/atoms/Price";
import { useRange } from "src/molecules/Range";

interface Props {
  min: number;
  max: number;
  currentRefinement: {
    min: number;
    max: number;
  };
  canRefine: boolean;
  refine: ({ min, max }: { min: number; max: number }) => void;
}
const RangeSlider: FC<Props> = ({
  min,
  max,
  currentRefinement,
  canRefine,
  refine,
}) => {
  const [stateMin, setStateMin] = useState(currentRefinement.min);
  const [stateMax, setStateMax] = useState(currentRefinement.max);

  useEffect(() => {
    if (canRefine) {
      setStateMin(currentRefinement.min);
      setStateMax(currentRefinement.max);
    }
  }, [currentRefinement.min, currentRefinement.max, canRefine]);

  const floorMin = Math.floor(currentRefinement.min || 0);
  const ceilMax = Math.ceil(currentRefinement.max || 0);
  const { onSliderChange, initialMinPrice, initialMaxPrice, range } = useRange({
    priceFilter: { min: Math.floor(min), max: Math.ceil(max) },
    minPrice: floorMin,
    maxPrice: ceilMax,
    handleRange: ([start, end]) => {
      refine({ min: Math.max(start, min), max: Math.min(end, max) });
    },
  });

  const theme: Theme = useTheme();
  return (
    <Box pt={1}>
      <Flex my={2} justifyContent="space-between">
        <Text fontWeight={600}>
          <Price price={Math.floor(stateMin)} />
        </Text>
        <Text fontWeight={600}>
          <Price price={Math.ceil(stateMax)} />
        </Text>
      </Flex>
      <Range
        trackStyle={[
          { backgroundColor: theme.colors.tone.black },
          { backgroundColor: theme.colors.tone.marble },
        ]}
        handleStyle={[
          {
            backgroundColor: theme.colors.tone.white,
            border: `2px solid ${theme.colors.tone.black}`,
            height: "22px",
            width: "22px",
            top: "1px",
            boxShadow: "none",
          },
          {
            backgroundColor: theme.colors.tone.white,
            border: `2px solid ${theme.colors.tone.black}`,
            height: "22px",
            width: "22px",
            top: "1px",
            boxShadow: "none",
          },
        ]}
        railStyle={{ backgroundColor: theme.colors.tone.marble }}
        onChange={onSliderChange}
        onAfterChange={([start, end]) =>
          refine({ min: Math.max(start, min), max: Math.min(end, max) })
        }
        defaultValue={[initialMinPrice, initialMaxPrice]}
        allowCross={false}
        min={initialMinPrice}
        max={initialMaxPrice}
        value={[Number(range[0]), Number(range[1])]}
      />
    </Box>
  );
};

const CustomRangeSlider = connectRange(RangeSlider);
export default CustomRangeSlider;
